import { IComponentOptions, IScope } from "angular";

import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IAssignee } from "@gtmhub/assignees/models";
import { IAccess, IApiPermission } from "@gtmhub/sessions";
import { IRole, RoleService } from "@gtmhub/roles";
import { AssigneeActions, IAssigneesState, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IdMap, toIdMap } from "@gtmhub/util";
import { INgRedux } from "@gtmhub/state-management";
import { getCurrentUserId } from "@gtmhub/users";

const bindStateToThis = (state: IAssigneesStoreState): Record<string, unknown> => {
  return {
    assigneesState: state.assignees,
    assignees: state.assignees.map,
  };
};

enum AccessTypes {
  publicRead = "publicRead",
  publicUpdate = "publicUpdate",
  private = "private",
  restricted = "restricted",
}

type Principal = (IAssignee | IRole) & { inUse?: boolean };

export const principalKindByCollection = {
  users: "user",
  teams: "team",
  roles: "role",
};

export class SimplePermissionsCtrl extends GtmhubController {
  // bindings
  access: IAccess;
  ownerId: string;
  onChange: ({ access: IAccess }) => void;

  principalsMap: { [id: string]: Principal };
  accessType: AccessTypes;
  assigneesState: IAssigneesState;
  assignees: IdMap<IAssignee>;
  principalsInUse: { [id: string]: boolean };
  principals: Principal[];
  principalSearch: string;

  searchCollections = ["teams", "employees", "roles"];
  totalNumberOfItems = 12;

  static $inject = ["$scope", "AccountResolverService", "RoleService", "UIErrorHandlingService", "AssigneeActions", "$ngRedux"];

  constructor(
    private $scope: IScope & IExtendedRootScopeService,
    private accountResolverService: AccountResolverService,
    private roleService: RoleService,
    private uiErrorHandlingService: UIErrorHandlingService,
    assigneeActions: AssigneeActions,
    $ngRedux: INgRedux
  ) {
    super();

    this.onDestroy($ngRedux.connect(bindStateToThis)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());
  }

  accessTypes = AccessTypes;

  $onInit(): void {
    this.determineAccessType();

    this.$scope
      .watchUntil(() => this.assigneesState.isFetched)
      .then(() => {
        this.getRoles();
      });
  }

  getRoles(): void {
    this.roleService.getRoles().then(
      (roles) => {
        this.principals = [...Object.values(this.assignees), ...roles.items];
        this.principalsMap = { ...this.assignees, ...toIdMap(roles.items) };

        this.markUsedPrincipals();
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  }

  determineAccessType(): void {
    for (const p of this.access.permissions) {
      if (p.principalKind === "account" && p.grant.general.indexOf("read") !== -1 && p.grant.general.indexOf("update") === -1) {
        this.accessType = AccessTypes.publicRead;
        return;
      }

      if (p.principalKind === "account" && p.grant.general.indexOf("read") !== -1 && p.grant.general.indexOf("update") !== -1) {
        this.accessType = AccessTypes.publicUpdate;
        return;
      }
    }

    if (this.access.permissions.length === 0) {
      this.accessType = AccessTypes.private;
      return;
    }

    this.accessType = AccessTypes.restricted;
  }

  markUsedPrincipals(): void {
    this.principalsInUse = {};

    for (const p of this.access.permissions) {
      this.principalsInUse[p.principalId] = true;
    }

    for (const m of this.principals) {
      if (this.principalsInUse[m.id]) {
        m.inUse = true;
      } else {
        m.inUse = false;
      }
    }
  }

  changeGrant = (changeTo: string, permission: IApiPermission): void => {
    for (const p of this.access.permissions) {
      if (p.principalId === permission.principalId) {
        if (changeTo === "update") {
          p.grant.general = ["read", "update"];
        }
        if (changeTo === "read") {
          p.grant.general = ["read"];
        }
      }
    }

    this.onChange({ access: this.access });
  };

  changeAccessType(type: AccessTypes): void {
    if (type === AccessTypes.private) {
      this.access = {
        inherits: false,
        permissions: [],
      };
    }
    if (type === AccessTypes.restricted) {
      this.access = {
        inherits: false,
        permissions: [
          {
            principalKind: "user",
            grant: {
              general: ["read", "update", "delete", "create", "modifyPermissions"],
            },
            principalId: getCurrentUserId(),
          },
        ],
      };
    }
    if (type === AccessTypes.publicRead || type === AccessTypes.publicUpdate) {
      this.access = {
        inherits: false,
        permissions: [
          {
            principalKind: "user",
            grant: {
              general: ["read", "update", "delete", "create", "modifyPermissions"],
            },
            principalId: getCurrentUserId(),
          },
          {
            principalKind: "account",
            grant: {
              general: ["read"],
            },
            principalId: this.accountResolverService.getAccountId(),
          },
        ],
      };

      if (type === AccessTypes.publicUpdate) {
        this.access.permissions[1].grant.general = ["read", "update"];
      }
    }

    this.onChange({ access: this.access });
    this.determineAccessType();
  }

  addPrincipal(value: any): void {
    if (this.access.permissions.some((p) => p.principalId === value.id)) {
      return;
    }
    const newPermission: IApiPermission = {
      principalKind: principalKindByCollection[value.collectionName],
      grant: {
        general: ["read"],
      },
      principalId: value.id,
    };
    this.access.permissions.push(newPermission);
    this.principalSearch = "";
    this.markUsedPrincipals();

    this.onChange({ access: this.access });
  }

  removePermission(permission: IApiPermission): void {
    for (let i = 0; i < this.access.permissions.length; i++) {
      if (this.access.permissions[i].principalId === permission.principalId) {
        this.access.permissions.splice(i, 1);
        this.markUsedPrincipals();
        this.onChange({ access: this.access });
        return;
      }
    }
  }
}

export const SimplePermissionsComponent: IComponentOptions = {
  controller: SimplePermissionsCtrl,
  templateUrl: require("./simple-permissions.component.jade"),
  bindings: {
    permission: "<",
    access: "<",
    ownerId: "<",
    onChange: "&",
  },
};
